import React, { useMemo } from 'react';
import styled from 'styled-components';
import media from 'styles/media';
import { clamp, propsToDom } from 'core/helpers';
import { ContainerGrid } from 'components/Container';
import { Panel } from 'components/Panel';
import { Image } from 'components/Image';
import { RichText } from 'components/RichText';
import { useGlobalContext } from 'store/GlobalProvider';
import { Label, Title5 } from 'styles/typography';
import { transitionFillHighlight } from 'styles/transitions';
import useOffsetSize from 'hooks/useOffsetSize';
import { motion, useTransform } from 'framer-motion';
import { useViewportScroll } from 'hooks/useViewportScroll';
import { propsToLink } from 'core/helpers';
import ConditionalWrap from 'components/ConditionalWrap';
import { Link } from 'components/Link';

// ------------------------------------------------------------
//    CAROUSEL IMAGE
// ------------------------------------------------------------
const CarouselPushImageComponent = ({ index, height, progress, ...props }) => {
  const y = useTransform(progress, (p) => clamp(height * index - p, 0, height));
  return <Image Component={motion.div} {...props} style={{ y }} />;
};

const CarouselPushImage = styled(CarouselPushImageComponent)`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% + 1px);
  height: 100%;
  will-change: transform;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// ------------------------------------------------------------
//    CAROUSEL TEXT
// ------------------------------------------------------------
const CarouselPushTextComponent = ({
  item_title,
  text,
  link,
  progress,
  height,
  index,
  ...others
}) => {
  const hasTitle = !!item_title?.text;
  const hasText = !!text?.richText?.length > 0;
  const hasLink = !!link?.url;
  const [{ top: titleTop }, $title] = useOffsetSize();
  const y = useTransform(progress, (p) => -p);
  const opacity = useTransform(progress, [height * index, height * index + height * 0.65], [1, 0]);

  const base = height * index;
  const titleY = useTransform(progress, [base, base + titleTop], [0, titleTop]);

  return (
    <motion.div {...propsToDom(others)} style={{ y, opacity }}>
      {hasTitle && (
        <CarouselPushTextTitleContainer>
          <ConditionalWrap
            key={index}
            condition={hasLink}
            wrap={(child) => <Link {...propsToLink(link)}>{child}</Link>}
          >
            <Title5 as={motion.h3} ref={$title} style={{ y: titleY }}>
              {item_title.text}
            </Title5>
          </ConditionalWrap>
        </CarouselPushTextTitleContainer>
      )}
      {hasText && <RichText render={text.richText} />}
    </motion.div>
  );
};

const CarouselPushText = styled(CarouselPushTextComponent)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: stretch;
  height: var(--sticky-height);
  text-align: right;
`;

const CarouselPushTextTitleContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-grow: 5;

  ${Title5} {
    ${transitionFillHighlight};
  }

  @media (hover: hover) and (pointer: fine) {
    a:hover {
      ${Title5} {
        color: ${({ theme }) => theme.colors.accent};
      }
    }
  }
`;

// ------------------------------------------------------------
//    CAROUSEL MAIN
// ------------------------------------------------------------
const CarouselPushComponent = ({ primary, items, slice_type, next_slice_type, ...others }) => {
  const scrollY = useViewportScroll();
  const [{ height, top }, $sizer] = useOffsetSize();
  const [{ height: stickyHeight }, $left] = useOffsetSize();
  const { wh } = useGlobalContext();
  const { background, label } = primary;
  const projects = useMemo(() => items.filter(({ image }) => !!image?.url), [items]);
  const stickyTop = wh > 0 && stickyHeight > 0 && wh / 2 - stickyHeight / 2;
  const progress = useTransform(scrollY, (scrollY) =>
    clamp(scrollY - top + stickyTop, 0, height - stickyHeight)
  );

  return (
    <Panel
      {...propsToDom(others)}
      id={slice_type}
      nextId={next_slice_type}
      background={background}
      style={{
        '--length': projects.length,
        '--top': stickyTop && `${stickyTop}px`,
        '--sticky-height': stickyHeight && `${stickyHeight}px`,
      }}
    >
      <Container ref={$sizer}>
        <ContainerGrid>
          <Left ref={$left}>
            {projects.map(({ image, link }, index) => {
              const props = { ...image, dimensions: { width: 1880, height: 1058 } };
              const hasLink = !!link?.url;

              return (
                <ConditionalWrap
                  key={index}
                  condition={hasLink}
                  wrap={(child) => <Link {...propsToLink(link)}>{child}</Link>}
                >
                  <CarouselPushImage
                    {...props}
                    key={index}
                    index={index}
                    height={stickyHeight}
                    progress={progress}
                  />
                </ConditionalWrap>
              );
            })}
          </Left>
          <Right>
            <Label>{label.text}</Label>
            <div>
              {projects.map((props, index) => (
                <CarouselPushText
                  key={index}
                  {...props}
                  index={index}
                  height={stickyHeight}
                  progress={progress}
                />
              ))}
            </div>
          </Right>
        </ContainerGrid>
      </Container>
    </Panel>
  );
};

const Left = styled.div`
  position: relative;
  grid-column: 1 / span 6;
  align-self: flex-start;
  margin-left: calc(var(--margin) * -1);
  border-radius: 0 8px 8px 0;
  overflow: hidden;

  &:before {
    content: '';
    display: block;
    padding-bottom: ${(2 / 3) * 100}%;
  }

  ${media.tabletPortrait`
    &:before {
      padding-bottom: ${100}%;
    }
  `}
`;

const Right = styled.div`
  position: relative;
  grid-column: auto / span 2;

  ${Label} {
    position: absolute;
    right: 0;
    top: 0;
    text-align: right;
  }
`;

const Container = styled.div`
  position: relative;
  height: calc(var(--sticky-height) * var(--length));
`;

export const CarouselPushDesktop = styled(CarouselPushComponent)`
  --sticky-height: calc(var(--viewport-height, 100vh) - var(--scroll-padding-top, 0));

  ${ContainerGrid} {
    position: sticky;
    top: var(--top, 0);
    height: var(--sticky-height);
    overflow: hidden;
  }
`;
